import axiosInstant from "../api";

export interface FocusedSigningSessionEnd {
    sessionEndType?: string;
    [key: string]: any;
}

interface StartFocusedSigningOpts {
    /** Backend endpoint returning { url, integrationKey, jsUrl }. */
    signingUrlPath: string;
    /**
     * GETTER for the DOM node the DocuSign focused view mounts into, resolved
     * AFTER the async setup completes. A getter (not a value) because callers
     * typically flip state to render the container in the same tick they start
     * signing — reading ref.current at call time would capture null (the render
     * hasn't committed yet) and the mount would silently no-op.
     */
    getContainer: () => HTMLElement | null;
    onReady?: () => void;
    onSessionEnd?: (event: FocusedSigningSessionEnd) => void;
}

/**
 * DocuSign embedded-signing bootstrap for the SOW flow: fetch the single-use
 * signing URL, lazily inject the DocuSign JS bundle, and mount the focused
 * view. Used by ExpertSowSignModal for both expert signing and the company
 * countersignature. (The MSA profile tab deliberately keeps its own
 * production-proven copy of this logic — do not migrate it.)
 *
 * Throws on failure; the caller owns its own loading/error state.
 */
export const startFocusedSigning = async ({
    signingUrlPath,
    getContainer,
    onReady,
    onSessionEnd
}: StartFocusedSigningOpts): Promise<void> => {
    const response = await axiosInstant.get(signingUrlPath);
    const { url, integrationKey, jsUrl } = response.data;

    if (!(window as any).DocuSign) {
        await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = jsUrl;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load DocuSign JS"));
            document.head.appendChild(script);
        });
    }

    const docusign = await (window as any).DocuSign.loadDocuSign(integrationKey);

    const signing = docusign.signing({
        url,
        displayFormat: "default",
        style: {
            branding: {
                primaryButton: {
                    backgroundColor: "#333",
                    color: "#fff"
                }
            },
            signingNavigationButton: {
                finishText: "Complete Signing",
                position: "bottom-center"
            }
        }
    });

    if (onReady) signing.on("ready", onReady);
    if (onSessionEnd) signing.on("sessionEnd", onSessionEnd);

    // Resolved only now — after the URL fetch and bundle load, any render the
    // caller triggered before calling us has long since committed.
    const container = getContainer();
    if (!container) {
        throw new Error("Signing container is not mounted");
    }
    signing.mount(container);
};
